import React, { useState } from 'react';
import { MortgageRate, CreditScoreTier } from '../types';
import { creditSpreads } from '../data/rates';
import { InfoTooltip } from './InfoTooltip';
import { RateChart } from './RateChart';
import { useMortgageRates } from '../hooks/useMortgageRates';

interface RateSelectorProps {
    onRateSelect: (rate: MortgageRate) => void;
    // Optional props for controlled state (synchronization with parent)
    selectedCreditTier?: CreditScoreTier;
    onCreditTierChange?: (tier: CreditScoreTier) => void;
}

export const RateSelector: React.FC<RateSelectorProps> = ({ 
    onRateSelect, 
    selectedCreditTier, 
    onCreditTierChange 
}) => {
    const [selectedType, setSelectedType] = useState<string | null>(null);
    // Internal state for when the component is used in uncontrolled mode (e.g. BuyNewHomeCalculator)
    const [internalCreditTier, setInternalCreditTier] = useState<CreditScoreTier>(CreditScoreTier.EXCELLENT);
    
    const { rates, lastUpdated, isLoading } = useMortgageRates();

    // Use prop if provided (controlled), otherwise use internal state (uncontrolled)
    const activeCreditTier = selectedCreditTier !== undefined ? selectedCreditTier : internalCreditTier;

    const handleCreditChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newTier = e.target.value as CreditScoreTier;
        if (onCreditTierChange) {
            onCreditTierChange(newTier);
        } else {
            setInternalCreditTier(newTier);
        }
    };

    const handleSelect = (baseRate: MortgageRate) => {
        const adjustedRate = parseFloat((baseRate.rate + creditSpreads[activeCreditTier]).toFixed(3));
        const rateSelection = { ...baseRate, rate: adjustedRate };
        setSelectedType(rateSelection.type);
        onRateSelect(rateSelection);
    };

    return (
        <div className="bg-white dark:bg-slate-800 p-4 sm:p-6 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 mb-6 transition-colors">
            <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
                 <div className="flex items-center flex-wrap gap-2">
                    <h3 className="text-xl font-bold text-bp-slate dark:text-white">Today's Sample Rates</h3>
                    <span className={`text-sm font-medium px-2 py-0.5 rounded-full border ${isLoading ? 'bg-yellow-50 text-yellow-600 border-yellow-200' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-slate-600'}`}>
                        {isLoading ? 'Updating...' : `Updated: ${lastUpdated}`}
                    </span>
                    <InfoTooltip text="These are illustrative nationwide rates for owner-occupied single-family homes. Your actual rate may vary. Click a card to auto-fill your rate and term below." />
                </div>
                <div className="flex items-center gap-2">
                    <label htmlFor="rs-creditScore" className="text-sm font-semibold text-gray-700 dark:text-gray-300">Credit Score:</label>
                    <select
                        id="rs-creditScore"
                        value={activeCreditTier}
                        onChange={handleCreditChange}
                        className="p-2 border rounded-md bg-white dark:bg-slate-700 text-sm text-gray-800 dark:text-white dark:border-slate-600"
                    >
                        {Object.values(CreditScoreTier).map(tier => (
                            <option key={tier} value={tier}>{tier}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {rates.map(rate => {
                    const adjustedRate = (rate.rate + creditSpreads[activeCreditTier]).toFixed(3);
                    // Use the history attached to the rate object (from API or fallback)
                    const chartData = rate.history?.data || [];
                    const chartLabels = rate.history?.labels || [];
                    const isSelected = selectedType === rate.type;

                    return (
                        <div
                            key={rate.type}
                            onClick={() => handleSelect(rate)}
                            className={`p-3 rounded-lg transition-all duration-200 border-2 cursor-pointer ${
                                isSelected 
                                    ? 'bg-blue-50 dark:bg-blue-900/30 border-bp-dark-blue shadow-lg' 
                                    : 'bg-gray-50 dark:bg-slate-700 hover:border-bp-light-blue border-gray-200 dark:border-slate-600'
                            }`}
                        >
                            <div className="flex justify-between items-baseline mb-2">
                                <p className="font-bold text-base text-bp-slate dark:text-gray-200">{rate.type}</p>
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-bp-dark-blue dark:text-bp-light-blue">{adjustedRate}%</p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 -mt-1">{rate.term} Years</p>
                                </div>
                            </div>
                            <div className="mt-2 h-32">
                                <RateChart 
                                    data={chartData} 
                                    labels={chartLabels}
                                    lineColor={isSelected ? '#3387ae' : '#a0aec0'}
                                    areaColor={isSelected ? 'rgba(51, 135, 174, 0.1)' : 'rgba(160, 174, 192, 0.1)'}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};